import React, {
  useState,
  useMemo,
  useEffect,
  PropsWithChildren,
  useRef,
  RefObject,
  ComponentType,
  useCallback,
  Suspense,
} from 'react';
import IconButton from '@mui/material/IconButton';
import {
  QueryErrorResetBoundary,
} from '@tanstack/react-query';
import {
  useLocation,
  useNavigate,
} from 'react-router-dom';
import Drawer, {
  DrawerClasses,
} from '@mui/material/Drawer';
import isNil from 'lodash/isNil';
import clsx from 'clsx';
import BackwardIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import ToolTip from 'core/components/Tooltip';
import CopyLinkButton from 'pages/Dashboard/contexts/sidePanel/CopyLinkButton';
import PanelErrorBoundary from 'pages/Dashboard/contexts/sidePanel/PanelErrorBoundary';
import {
  ActivePanel,
  OpenPanelOptions,
  PanelInstance,
  PanelInstanceContext,
  SidePanelComponentProps,
  SidePanelContext,
} from 'pages/Dashboard/contexts/sidePanel';
import {
  clearPanelParams,
  getDefinition,
  getPanelSearch,
  getPanelUrl,
  PanelRegistry,
  parsePanelParams,
  setPanelParams,
} from 'pages/Dashboard/contexts/sidePanel/panelUrl';
import {
  queueUrlParamsUpdate,
} from 'utils/urlWriter';

type Panel = {
  // Remounts the component when it changes
  key: string;
  Component: ComponentType<SidePanelComponentProps<any>>;
  entity: any;
  classes?: Partial<DrawerClasses>;
  active: ActivePanel;
  // URL panels only: the search the panel was resolved from
  search?: string;
};

type Change = {
  memoryPanel: Panel | null;
  stack: Panel[];
};

type State = Change & {
  pathname: string;
  search: string;
  // Applied once the URL holds the expected panel params, dropped otherwise
  pending: { expectedPanelSearch: string; change: Change } | null;
};

type Display = {
  panel: Panel | null;
  // Goes up each time the drawer opens from closed, so a panel reopened
  // during its exit transition still remounts
  session: number;
  open: boolean;
};

function fromRegistry(registry: PanelRegistry, active: ActivePanel): Panel {
  const { Component, classes } = registry[active.type];
  return {
    key: `${active.type}:${active.entityKey}`,
    Component,
    entity: active.panelInfo,
    classes,
    active,
  };
}

function getUrlPanel(registry: PanelRegistry, search: string) {
  const active = parsePanelParams(new URLSearchParams(search), registry);
  return isNil(active) ? null : { ...fromRegistry(registry, active), search };
}

function isSamePanel(a: Panel | null, b: Panel | null) {
  return a?.key === b?.key;
}

function getNextState(state: State, registry: PanelRegistry, pathname: string, search: string): State {
  const next = { ...state, pathname, search };

  // The destination URL alone decides the panel
  if (state.pathname !== pathname) {
    return { ...next, memoryPanel: null, stack: [], pending: null };
  }

  const panelSearch = getPanelSearch(new URLSearchParams(search), registry);
  if (state.pending?.expectedPanelSearch === panelSearch) {
    return { ...next, ...state.pending.change, pending: null };
  }

  // Not our write: keep everything unless the URL panel's entity changed
  const isSameUrlPanel = isSamePanel(
    getUrlPanel(registry, state.search),
    getUrlPanel(registry, search),
  );
  return isSameUrlPanel
    ? { ...next, pending: null }
    : { ...next, memoryPanel: null, stack: [], pending: null };
}

function restoreFocus(element: HTMLElement | null) {
  if (!isNil(element) && document.contains(element)) {
    setTimeout(() => {
      element.focus({ preventScroll: true });
    }, 0);
  }
}

type ProviderProps = PropsWithChildren<{
  registry: PanelRegistry;
}>;

export default function DetailedViewContextProvider({
  registry,
  children,
}: ProviderProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const panelExtensionRef = useRef<HTMLDivElement | null>(null);
  const savedFocusRef = useRef<HTMLElement | null>(null);
  const [state, setState] = useState<State>({
    pathname: location.pathname,
    search: location.search,
    memoryPanel: null,
    stack: [],
    pending: null,
  });
  const [display, setDisplay] = useState<Display>({ panel: null, session: 0, open: false });

  // Applied during render so the panel changes in the same render as the URL
  if (state.pathname !== location.pathname || state.search !== location.search) {
    setState(getNextState(state, registry, location.pathname, location.search));
  }

  const urlPanel = useMemo(() => getUrlPanel(registry, state.search), [registry, state.search]);
  const shownPanel = state.memoryPanel ?? urlPanel;

  if (!isNil(shownPanel) && (!display.open || display.panel !== shownPanel)) {
    setDisplay({
      panel: shownPanel,
      session: display.open ? display.session : display.session + 1,
      open: true,
    });
  } else if (isNil(shownPanel) && display.open) {
    // Keeps rendering the last panel until the drawer's exit transition ends
    setDisplay({ ...display, open: false });
  }

  // Listeners registered once keep the first functions they get, so the
  // functions below are stable and read the latest values from refs. The refs
  // are set during render because child effects run before this one's.
  const stateRef = useRef(state);
  const registryRef = useRef(registry);
  const navigateRef = useRef(navigate);
  stateRef.current = state;
  registryRef.current = registry;
  navigateRef.current = navigate;

  const getShownPanel = () => (
    stateRef.current.memoryPanel ?? getUrlPanel(registryRef.current, window.location.search)
  );

  const queueChange = (
    expectedPanelSearch: string,
    change: Change,
    update: (searchParams: URLSearchParams) => void,
  ) => {
    const currentPanelSearch = getPanelSearch(
      new URLSearchParams(window.location.search),
      registryRef.current,
    );

    // Always queued, so it overrides panel writes queued earlier in this tick
    queueUrlParamsUpdate(update, navigateRef.current);

    if (currentPanelSearch === expectedPanelSearch) {
      setState((current) => ({ ...current, ...change, pending: null }));
    } else {
      setState((current) => ({ ...current, pending: { expectedPanelSearch, change } }));
    }
  };

  const showPanel = (panel: Panel, stack: Panel[]) => {
    const currentRegistry = registryRef.current;
    const { type, panelInfo } = panel.active;

    if (!isNil(getPanelUrl(currentRegistry, type, panelInfo))) {
      const draft = new URLSearchParams();
      setPanelParams(draft, type, panelInfo, currentRegistry);
      queueChange(
        getPanelSearch(draft, currentRegistry),
        { memoryPanel: null, stack },
        (searchParams) => setPanelParams(searchParams, type, panelInfo, currentRegistry),
      );
    } else {
      queueChange(
        '',
        { memoryPanel: panel, stack },
        (searchParams) => clearPanelParams(searchParams, currentRegistry),
      );
    }
  };

  const openPanel = useCallback((type: string, panelInfo: unknown, options: OpenPanelOptions = {}) => {
    const definition = getDefinition(registryRef.current, type);
    if (isNil(definition)) {
      throw new Error(`Unknown side panel "${type}"`);
    }

    const panel = fromRegistry(registryRef.current, {
      type,
      panelInfo,
      entityKey: definition.getPanelIdentity(panelInfo),
    });
    const shown = getShownPanel();
    const previous = !isNil(shown) && !isNil(options.previousPanelInfo)
      ? { ...shown, entity: options.previousPanelInfo, active: { ...shown.active, panelInfo: options.previousPanelInfo } }
      : shown;
    const stack = options.canGoBack && !isNil(previous) ? [...stateRef.current.stack, previous] : [];

    savedFocusRef.current = document.activeElement as HTMLElement;
    showPanel(panel, stack);
  }, []);

  const closePanel = useCallback(() => {
    const currentRegistry = registryRef.current;
    queueChange(
      '',
      { memoryPanel: null, stack: [] },
      (searchParams) => clearPanelParams(searchParams, currentRegistry),
    );
  }, []);

  const goBack = useCallback(() => {
    const { stack } = stateRef.current;
    const previous = stack.at(-1);
    if (!isNil(previous)) {
      showPanel(previous, stack.slice(0, -1));
    }
  }, []);

  useEffect(() => {
    if (!display.open) {
      const elementToFocus = savedFocusRef.current;
      savedFocusRef.current = null;
      restoreFocus(elementToFocus);
    }
  }, [display.open]);

  const { panel } = display;
  const canGoBack = state.stack.length > 0;

  const panelInstance = useMemo<PanelInstance | null>(() => {
    if (isNil(panel) || isNil(panel.search)) {
      return null;
    }
    const { type, entityKey } = panel.active;
    return {
      params: registry[type].url?.params ?? [],
      search: panel.search,
      queueUrlParamsUpdate: (update) => {
        queueUrlParamsUpdate((searchParams) => {
          const current = parsePanelParams(searchParams, registryRef.current);
          if (current?.type === type && current.entityKey === entityKey) {
            update(searchParams);
          }
        }, navigateRef.current);
      },
    };
  }, [panel, registry]);

  const value = useMemo(() => ({
    activePanel: shownPanel?.active ?? null,
    openPanel,
    closePanel,
    goBack,
  }), [shownPanel]);

  const classes = panel?.classes;
  const panelKey = `${panel?.key}:${display.session}`;
  const backButton = canGoBack ? (
    <ToolTip title="Go Back">
      <IconButton
        size="small"
        aria-label="Go Back"
        className="!mr-2"
        onClick={goBack}
      >
        <BackwardIcon className="!w-5 !h-5" />
      </IconButton>
    </ToolTip>
  ) : null;

  return (
    <SidePanelContext.Provider value={value}>
      <Drawer
        anchor="right"
        open={display.open}
        onClose={closePanel}
        hideBackdrop
        classes={{
          ...classes,
          root: clsx(classes?.root, 'detailed-entity-drawer has-extension !overflow-[initial]'),
        }}
        slotProps={{
          transition: {
            onExited: () => {
              setDisplay((current) => (current.open ? current : { ...current, panel: null }));
            },
          },
        }}
        disableEnforceFocus
        disableScrollLock
        disableRestoreFocus
      >
        <div className="w-full h-full flex relative">
          {!isNil(panel) && (
            <PanelInstanceContext.Provider value={panelInstance}>
              <QueryErrorResetBoundary>
                {({ reset }) => (
                  <PanelErrorBoundary
                    resetKey={panelKey}
                    backButton={backButton}
                    recordName={registry[panel.active.type].getRecordName?.(panel.entity)}
                    onClose={closePanel}
                    onReset={reset}
                  >
                    <Suspense>
                      <panel.Component
                        key={panelKey}
                        entity={panel.entity}
                        onClose={closePanel}
                        slots={{
                          header: backButton,
                          actions: isNil(panel.search) ? null : <CopyLinkButton />,
                        }}
                        extensionRef={panelExtensionRef as RefObject<HTMLDivElement>}
                      />
                    </Suspense>
                  </PanelErrorBoundary>
                )}
              </QueryErrorResetBoundary>
            </PanelInstanceContext.Provider>
          )}
          <div ref={panelExtensionRef as RefObject<HTMLDivElement>} />
        </div>
      </Drawer>
      {children}
    </SidePanelContext.Provider>
  );
}
